import React, { useCallback, useEffect, useRef, useState } from 'react'
import { usePrevious } from 'react-use'
import { intl } from 'lang/intl'

import { TtButton, TtIcon } from '@23/react'

import { useWebinarColors } from '../../../../hooks/useWebinarColors'

import './join-to-speak-item.scss'

type JoinToSpeakItemProps = {
  attendeeId: string
  attendeeName: string
  attendeeFullName: string
  attendeeEmail: string
  question: string
  handleAttendeePromotion: (attendeeId: string) => Promise<void>
  handleAttendeeRejection: (attendeeId: string) => Promise<void>
  previewImageUrl: string
}

async function getPreviewImageObjectUrl(url: string): Promise<string | null> {
  const response = await fetch(url)
  if (response.status !== 200) {
    return null
  }

  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

const JoinToSpeakItem = ({
  attendeeId,
  attendeeName,
  attendeeFullName,
  attendeeEmail,
  question,
  handleAttendeePromotion,
  handleAttendeeRejection,
  previewImageUrl,
}: JoinToSpeakItemProps) => {
  const webinarColors = useWebinarColors()
  const intervalTimerRef = useRef<number | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isDeclining, setIsDeclining] = useState(false)
  const [previewImageObjectUrl, setPreviewImageObjectUrl] = useState<
    string | null
  >(null)
  const previousPreviewImageObjectUrl =
    usePrevious(previewImageObjectUrl) ?? null

  useEffect(() => {
    if (intervalTimerRef.current !== null) {
      clearInterval(intervalTimerRef.current)
      intervalTimerRef.current = null
    }

    const handleInterval = async () => {
      setPreviewImageObjectUrl(await getPreviewImageObjectUrl(previewImageUrl))
      if (previousPreviewImageObjectUrl === null) {
        return
      }
      URL.revokeObjectURL(previousPreviewImageObjectUrl)
    }
    void handleInterval()

    intervalTimerRef.current = window.setInterval(handleInterval, 5000)
  }, [previewImageUrl, previousPreviewImageObjectUrl])

  const handleAccept = useCallback(async () => {
    setIsAccepting(true)
    await handleAttendeePromotion(attendeeId)
  }, [attendeeId, handleAttendeePromotion])

  const handleDecline = useCallback(async () => {
    setIsDeclining(true)
    await handleAttendeeRejection(attendeeId)
  }, [attendeeId, handleAttendeeRejection])

  let dynamicName = attendeeFullName
  if (dynamicName !== attendeeName) {
    dynamicName += ` ${intl.formatMessage(
      {
        defaultMessage: '(shown as {name})',
      },
      { name: attendeeName }
    )}`
  }

  return (
    <div className="join-to-speak-item">
      <div className="join-to-speak-item-container">
        <div className="preview">
          {previewImageObjectUrl ? (
            <img
              className="preview-image"
              src={previewImageObjectUrl}
              alt={intl.formatMessage(
                {
                  defaultMessage: `camera preview of {attendeeName}`,
                },
                { attendeeName }
              )}
            />
          ) : (
            <div
              className="preview-fallback"
              style={{
                color: webinarColors.sidebar.contrast.color,
                backgroundColor:
                  webinarColors.sidebar.contrast.transparent('10%'),
              }}
            >
              <TtIcon name="camera-off" library="23" />
            </div>
          )}
        </div>
        <div className="request">
          <p className="attendee">
            <span className="name">{dynamicName}</span>
          </p>
          <p className="attendee-email">{attendeeEmail}</p>
          {question && question.length > 0 && (
            <p className="question">{question}</p>
          )}
        </div>
        <div className="controls">
          <TtButton
            label={intl.formatMessage(
              {
                defaultMessage: "Refuse {name}'s OnCamera request ",
              },
              { name: attendeeName }
            )}
            circle
            size="small"
            color="danger"
            onClick={handleDecline}
            disabled={isDeclining}
          >
            <TtIcon name="close" library="one" />
          </TtButton>
          <TtButton
            label={intl.formatMessage(
              {
                defaultMessage: "Accept {name}'s OnCamera request ",
              },
              { name: attendeeName }
            )}
            size="small"
            circle
            onClick={handleAccept}
            disabled={isAccepting}
          >
            <TtIcon name="check" library="one" />
          </TtButton>
        </div>
      </div>
    </div>
  )
}

export { JoinToSpeakItem }
